/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Finance Tab - Modular component for financial management
 */

import React from 'react';
import { CityStats, SimulationBreakdown, Grid, BuildingType } from '../../../types';
import { playUiSfx } from '../../../utils/uiAudio';

interface FinanceTabProps {
  stats: CityStats;
  simBreakdown?: SimulationBreakdown;
  hasJumpGate: boolean;
  onTakeLoan: () => void;
  onRepayLoan: () => void;
  onPitchInvestors: () => void;
}

const FinanceTab: React.FC<FinanceTabProps> = ({ 
  stats, 
  simBreakdown, 
  hasJumpGate, 
  onTakeLoan, 
  onRepayLoan, 
  onPitchInvestors 
}) => {
  const canPitch = hasJumpGate && stats.day - stats.lastPitchDay >= 25;
  const daysUntilPitch = Math.max(0, 25 - (stats.day - stats.lastPitchDay));

  return (
    <div className="space-y-6">
      <div className="border-b border-white/10 pb-4">
        <h3 className="text-lg font-bold text-white uppercase tracking-wider">Capital Controls</h3>
      </div>

      {/* Financial Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-900/30 to-green-900/30 border border-emerald-700/30 rounded-lg p-4">
          <div className="text-xs text-emerald-400 uppercase mb-1">Current Budget</div>
          <div className="text-2xl font-black text-white">${stats.budget.toLocaleString()}</div>
        </div>
        <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border border-cyan-700/30 rounded-lg p-4">
          <div className="text-xs text-cyan-400 uppercase mb-1">Day</div>
          <div className="text-2xl font-black text-white">{stats.day}</div>
        </div>
        <div className="bg-gradient-to-br from-red-900/30 to-orange-900/30 border border-red-700/30 rounded-lg p-4">
          <div className="text-xs text-red-400 uppercase mb-1">Active Loans</div>
          <div className="text-2xl font-black text-white">{stats.loanCount}</div>
        </div>
        <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 border border-purple-700/30 rounded-lg p-4">
          <div className="text-xs text-purple-400 uppercase mb-1">Interest/Tick</div>
          <div className="text-2xl font-black text-red-400">-${(stats.loanCount * 5000).toLocaleString()}</div>
        </div>
      </div>

      {/* Income Statement */}
      {simBreakdown && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Revenue */}
          <div className="bg-slate-900/30 border border-white/10 rounded-lg p-5">
            <h4 className="text-sm font-bold text-emerald-400 uppercase mb-4 flex items-center gap-2">
              <span>📈</span> Revenue Streams
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-white/5 rounded">
                <div className="flex items-center gap-2">
                  <span>🏢</span>
                  <span className="text-slate-300">Infrastructure Output</span>
                </div>
                <span className="font-mono text-emerald-400 font-bold">+${simBreakdown.income.buildings}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white/5 rounded">
                <div className="flex items-center gap-2">
                  <span>📦</span>
                  <span className="text-slate-300">Product Sales</span>
                </div>
                <span className="font-mono text-emerald-400 font-bold">+${simBreakdown.income.products}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-emerald-900/20 rounded border border-emerald-700/30">
                <span className="text-white font-bold">Total Income</span>
                <span className="font-mono text-emerald-400 font-bold text-lg">+${simBreakdown.income.total}</span>
              </div>
            </div>
          </div>

          {/* Expenses */}
          <div className="bg-slate-900/30 border border-white/10 rounded-lg p-5">
            <h4 className="text-sm font-bold text-red-400 uppercase mb-4 flex items-center gap-2">
              <span>📉</span> Operating Expenses
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-white/5 rounded">
                <div className="flex items-center gap-2">
                  <span>🔧</span>
                  <span className="text-slate-300">Maintenance</span>
                </div>
                <span className="font-mono text-red-400 font-bold">-${simBreakdown.expenses.maintenance}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white/5 rounded">
                <div className="flex items-center gap-2">
                  <span>💳</span>
                  <span className="text-slate-300">Loan Interest</span>
                </div>
                <span className="font-mono text-red-400 font-bold">-${simBreakdown.expenses.interest}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-red-900/20 rounded border border-red-700/30">
                <span className="text-white font-bold">Total Expenses</span>
                <span className="font-mono text-red-400 font-bold text-lg">-${simBreakdown.expenses.total}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Net Profit/Loss */}
      {simBreakdown && (
        <div className={`p-5 rounded-lg border ${
          simBreakdown.income.total - simBreakdown.expenses.total >= 0 
            ? 'bg-emerald-900/20 border-emerald-700/30' 
            : 'bg-red-900/20 border-red-700/30'
        }`}>
          <div className="flex justify-between items-center">
            <div>
              <div className="text-sm text-slate-400 uppercase">Net Profit/Loss per Tick</div>
              <div className={`text-3xl font-black ${
                simBreakdown.income.total - simBreakdown.expenses.total >= 0 
                  ? 'text-emerald-400' 
                  : 'text-red-400'
              }`}>
                {simBreakdown.income.total - simBreakdown.expenses.total >= 0 ? '+' : ''}
                ${simBreakdown.income.total - simBreakdown.expenses.total}
              </div>
            </div>
            <div className="text-6xl opacity-20">
              {simBreakdown.income.total - simBreakdown.expenses.total >= 0 ? '📈' : '📉'}
            </div>
          </div>
        </div>
      )}

      {/* Loan Management */}
      <div className="bg-slate-900/30 border border-white/10 rounded-lg p-5">
        <h4 className="text-sm font-bold text-white uppercase mb-4 flex items-center gap-2">
          <span>🏦</span> Loan Management
        </h4>
        <div className="grid md:grid-cols-2 gap-4">
          <button
            onClick={() => { playUiSfx('money'); onTakeLoan(); }}
            className="p-4 bg-emerald-900/20 border border-emerald-700/30 rounded-lg hover:bg-emerald-900/30 transition-colors text-left"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                <span className="text-xl">💵</span>
              </div>
              <div>
                <h4 className="text-white font-bold">Take Loan</h4>
                <p className="text-xs text-emerald-400">+$100,000 instant</p>
              </div>
            </div>
            <p className="text-sm text-slate-400">
              $5,000 interest per tick. Use for emergency funding or rapid expansion.
            </p>
          </button>

          <button
            onClick={() => { playUiSfx('money'); onRepayLoan(); }}
            disabled={stats.loanCount === 0 || stats.budget < 100000}
            className={`p-4 border rounded-lg text-left transition-colors ${
              stats.loanCount > 0 && stats.budget >= 1000 
                ? 'bg-blue-900/20 border-blue-700/30 hover:bg-blue-900/30' 
                : 'bg-slate-900/20 border-slate-700/30 opacity-50 cursor-not-allowed'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-xl">💳</span>
              </div>
              <div>
                <h4 className="text-white font-bold">Repay Loan</h4>
                <p className="text-xs text-blue-400">-$100,000</p>
              </div>
            </div>
            <p className="text-sm text-slate-400">
              Reduce interest burden. {stats.loanCount} loan(s) remaining.
            </p>
          </button>
        </div>
      </div>

      {/* Investment Rounds */}
      <div className="bg-gradient-to-r from-indigo-900/20 to-purple-900/20 border border-indigo-700/30 rounded-lg p-5">
        <h4 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
          <span>🚀</span> Funding Rounds
        </h4>
        
        {/* Funding Milestones */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          {[
            { name: 'Pre-Seed', amount: '$750K', target: 750000, icon: '🌱', color: 'emerald' },
            { name: 'Seed', amount: '$2M', target: 2000000, icon: '🌿', color: 'cyan' },
            { name: 'Series A', amount: '$10M', target: 10000000, icon: '🌳', color: 'purple' },
            { name: 'IPO', amount: '$50M', target: 50000000, icon: '🏆', color: 'yellow' },
          ].map((round, idx) => {
            const prevTargets = [0, 750000, 2000000, 10000000];
            const isCompleted = stats.budget >= round.target;
            const isCurrent = !isCompleted && (idx === 0 || stats.budget >= prevTargets[idx]);
            const progress = isCurrent ? Math.min(100, Math.round(((stats.budget - prevTargets[idx]) / (round.target - prevTargets[idx])) * 100)) : 0;
            
            return (
              <div 
                key={round.name}
                className={`p-3 rounded-lg border text-center transition-all ${
                  isCompleted 
                    ? 'bg-emerald-900/30 border-emerald-500/50' 
                    : isCurrent 
                      ? 'bg-cyan-900/20 border-cyan-500/30' 
                      : 'bg-black/20 border-white/5 opacity-50'
                }`}
              >
                <div className="text-2xl mb-1">{round.icon}</div>
                <div className={`text-xs font-bold uppercase ${isCompleted ? 'text-emerald-400' : isCurrent ? 'text-cyan-400' : 'text-slate-400'}`}>
                  {round.name}
                </div>
                <div className={`text-sm font-bold ${isCompleted ? 'text-white' : 'text-slate-500'}`}>
                  {round.amount}
                </div>
                {isCompleted && <div className="text-emerald-400 text-xs mt-1">✓ Achieved</div>}
                {isCurrent && (
                  <div className="mt-2">
                    <div className="w-full bg-slate-700 rounded-full h-1.5 mb-1">
                      <div className="bg-cyan-500 h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }}></div>
                    </div>
                    <div className="text-cyan-400 text-xs">{progress}% to goal</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Investor Pitch Button */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pt-4 border-t border-white/10">
          <div>
            <h4 className="text-white font-bold mb-1 flex items-center gap-2">
              <span>🎤</span> Pitch to Investors
            </h4>
            <p className="text-sm text-slate-400">
              {!hasJumpGate 
                ? '🔒 Requires Admin Jump Gate to access investors' 
                : canPitch 
                  ? '✅ Ready to pitch! Higher compliance = better funding chances'
                  : `⏳ Cooldown: ${daysUntilPitch} days remaining`
              }
            </p>
            {hasJumpGate && (
              <div className="mt-2 text-xs text-slate-500">
                Success chance: {Math.round(40 + (stats.compliance / 100) * 60)}% based on compliance
              </div>
            )}
          </div>
          <button
            onClick={() => { playUiSfx('click'); onPitchInvestors(); }}
            disabled={!canPitch}
            className={`px-6 py-3 rounded-lg font-bold uppercase tracking-wider text-sm transition-all whitespace-nowrap ${
              canPitch
                ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg hover:shadow-indigo-500/25'
                : 'bg-slate-700 text-slate-400 cursor-not-allowed'
            }`}
          >
            {canPitch ? '🚀 Pitch Now' : hasJumpGate ? `Wait ${daysUntilPitch}d` : '🔒 Locked'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinanceTab;
